import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, ArrowUpRight, Calendar } from 'lucide-react';
import { eventsAPI } from '../api';

const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const UpcomingEventsStrip = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    eventsAPI.upcoming(6).then((r) => setEvents(r.data || [])).catch(() => setEvents([])).finally(() => setLoading(false));
  }, []);

  if (!loading && events.length === 0) return null;

  return (
    <section className="relative py-24" data-testid="upcoming-events-strip">
      <div className="aurora-orb w-[420px] h-[420px] top-10 right-1/3 bg-indigo-200/50" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-sky-600 mb-3">What's On</p>
            <h2 className="text-5xl md:text-6xl font-bold tracking-[-0.04em] text-slate-900 leading-none">
              Upcoming <span className="font-serif-accent italic font-light bg-gradient-to-r from-orange-400 to-fuchsia-500 bg-clip-text text-transparent">events</span>
            </h2>
          </div>
          <Link to="/events" className="hidden sm:inline-flex items-center gap-2 glass-strong glass-hover rounded-full px-5 py-2.5 text-sm font-semibold text-slate-800">
            Full calendar <ArrowUpRight size={15} />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <div key={i} className="h-44 glass rounded-3xl animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {events.map((ev) => (
              <Link
                key={ev.id}
                to={`/events/${ev.slug}`}
                className="group glass rounded-3xl p-5 glass-hover flex gap-4 items-start"
                data-testid={`home-event-card-${ev.slug}`}
              >
                {/* Gradient date block */}
                <div className="shrink-0 rounded-2xl px-3 py-3 text-center bg-gradient-to-br from-sky-500 via-indigo-500 to-fuchsia-500 text-white shadow-lg shadow-sky-500/30 min-w-[64px]">
                  <p className="text-[10px] uppercase tracking-widest font-bold opacity-90">
                    {new Date(ev.start_date).toLocaleString('en-GB', { month: 'short' })}
                  </p>
                  <p className="text-2xl font-extrabold leading-none mt-0.5">{new Date(ev.start_date).getDate()}</p>
                </div>
                <div className="flex-1 min-w-0">
                  {ev.featured && (
                    <span className="text-[10px] uppercase font-bold tracking-widest text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded mb-1 inline-block">★ Featured</span>
                  )}
                  <h3 className="text-base font-bold text-slate-900 group-hover:text-sky-600 transition-colors leading-snug line-clamp-2">
                    {ev.title}
                  </h3>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
                    <span className="flex items-center gap-1"><Clock size={11} />{fmtTime(ev.start_date)}</span>
                    {ev.venue && <span className="flex items-center gap-1 truncate"><MapPin size={11} /><span className="truncate">{ev.venue}</span></span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="sm:hidden mt-8 text-center">
          <Link to="/events" className="font-semibold text-sky-600">Full calendar →</Link>
        </div>
      </div>
    </section>
  );
};

export default UpcomingEventsStrip;
